import React, { useCallback } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/shared/toggle-group';
import { DimensionQuestion } from '@/types/readiness';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shared/tooltip';

interface QuestionResponseProps {
  question: DimensionQuestion;
  value: string;
  onChange: (value: string) => void;
}

const QuestionResponse = ({ question, value, onChange }: QuestionResponseProps) => {
  console.log('QuestionResponse render:', { question, value });
  
  const handleValueChange = useCallback((newValue: string) => {
    console.log('Toggle value changed:', newValue);
    // Only call onChange if there's a new value to prevent deselection
    if (newValue) {
      onChange(newValue);
    }
  }, [onChange]);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <p className="text-base font-medium text-[#222222]">{question.question}</p>
        {question.guidance && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="mt-1 focus:outline-none">
                  <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              </TooltipTrigger>
              <TooltipContent 
                side="right" 
                sideOffset={5}
                className="bg-white text-black border-none shadow-lg"
              >
                <p className="max-w-xs text-sm">{question.guidance}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      <ToggleGroup 
        type="single" 
        value={value}
        onValueChange={handleValueChange}
        className="flex flex-col gap-2"
      >
        {question.options.map((option, index) => {
          const isSelected = value === option;
          return (
            <ToggleGroupItem 
              key={index}
              value={option}
              aria-label={option}
              data-state={isSelected ? 'on' : 'off'}
              data-testid={`toggle-option-${index}`}
              className={`w-full px-4 py-3 text-sm font-medium text-left transition-colors rounded-lg border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                ${isSelected 
                  ? 'bg-primary text-primary-foreground border-primary' 
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-primary/10'
                }`}
            >
              {option}
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </div>
  );
};

export default React.memo(QuestionResponse);